import { AppState } from '../reducers';
import * as Services from '../../services/ImportJobs.Services';
import {
  IMPORT_JOBS_FOREGROUND,
  IMPORT_JOBS_INIT,
  IMPORT_JOBS_NOTICE_DONE,
  IMPORT_JOBS_POLLING,
  IMPORT_JOBS_TRACK,
  IMPORT_JOBS_UPDATED,
  ImportJobsKnownAction,
} from '../actions/ImportJobs.Actions';
import { activeImportJobs } from '../reducers/ImportJobs.Reducer';
import {
  IMPORT_JOBS_WATCH_POLL_MS,
  importJobsPollSince,
  loadNotifiedJobIds,
  loadOrStartSessionSince,
  saveNotifiedJobIds,
} from '../../functions/ImportJobs.Functions';
import { ProviderImportJob } from '../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ErrorType } from '../../models/HttpError';
import { toErrorType } from './ProviderConsumptionImport.Middleware';

/**
 * Aviso global de las importaciones en segundo plano (spec 008). El token se lee sólo acá.
 * Consulta `GET import-jobs/mine?activeOrSince=` cada 5 s **sólo mientras haya jobs activos conocidos**
 * (setTimeout encadenado: una consulta por vez; se detiene sola cuando no quedan activos).
 */

type Dispatch = (action: ImportJobsKnownAction | ImportJobsThunk<unknown>) => unknown;
export type ImportJobsThunk<TResult> = (dispatch: Dispatch, getState: () => AppState) => TResult;

const NotAuthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] };

const tokenOf = (getState: () => AppState): string | undefined => getState().oidc?.user?.access_token;

const requireToken = (getState: () => AppState): string => {
  const token = tokenOf(getState);
  if (!token) throw NotAuthorized;
  return token;
};

const run = async <T>(getState: () => AppState, call: (token: string) => Promise<T>): Promise<T> => {
  const token = requireToken(getState);
  try {
    return await call(token);
  } catch (error: unknown) {
    throw toErrorType(error);
  }
};

// ---- polling (un solo timer para toda la app) ----
let pollTimer: ReturnType<typeof setTimeout> | undefined;
let pollGeneration = 0;

const hasActiveJobs = (getState: () => AppState): boolean => activeImportJobs(getState().ImportJobs).length > 0;

/** Una consulta: `mine` y, por id, los activos conocidos que no vinieron (otros usuarios / reloj desfasado). */
const pollOnce = async (dispatch: Dispatch, getState: () => AppState): Promise<void> => {
  const token = tokenOf(getState);
  if (!token) return;
  const state = getState().ImportJobs;
  const known = Object.values(state?.jobs ?? {});
  const since = importJobsPollSince(known, state?.sessionSince ?? new Date().toISOString());
  const mine = await Services.GetMyImportJobs(token, since);
  dispatch({ type: IMPORT_JOBS_UPDATED, jobs: mine, notify: true });
  const returned = new Set(mine.map((job) => String(job.id)));
  const missing = activeImportJobs(getState().ImportJobs).filter((job) => !returned.has(String(job.id)));
  const fetched = await Promise.all(missing.map((job) => Services.GetImportJob(token, String(job.id)).catch(() => undefined)));
  const found = fetched.filter((job): job is ProviderImportJob => !!job);
  if (found.length > 0) dispatch({ type: IMPORT_JOBS_UPDATED, jobs: found, notify: false });
};

const schedule = (dispatch: Dispatch, getState: () => AppState) => {
  if (pollTimer !== undefined) return;
  const generation = pollGeneration;
  dispatch({ type: IMPORT_JOBS_POLLING, polling: true });
  pollTimer = setTimeout(async () => {
    pollTimer = undefined;
    if (generation !== pollGeneration) return;
    try {
      await pollOnce(dispatch, getState);
    } catch {
      // un error de red no corta el seguimiento: se reintenta en la próxima vuelta
    }
    if (generation !== pollGeneration) return;
    if (hasActiveJobs(getState)) schedule(dispatch, getState);
    else dispatch({ type: IMPORT_JOBS_POLLING, polling: false });
  }, IMPORT_JOBS_WATCH_POLL_MS);
};

const stop = (dispatch: Dispatch) => {
  pollGeneration++;
  if (pollTimer !== undefined) clearTimeout(pollTimer);
  pollTimer = undefined;
  dispatch({ type: IMPORT_JOBS_POLLING, polling: false });
};

export const actionCreators = {
  /**
   * Arranque (una vez, al montar el layout con usuario): ids avisados de sessionStorage, inicio de la sesión y
   * los jobs del usuario activos o terminados desde entonces; si hay activos, empieza a consultar.
   */
  InitImportJobsWatcher: (): ImportJobsThunk<Promise<void>> => async (dispatch, getState) => {
    if (getState().ImportJobs?.initialized) return;
    const sessionSince = loadOrStartSessionSince();
    dispatch({ type: IMPORT_JOBS_INIT, notifiedIds: loadNotifiedJobIds(), sessionSince });
    const token = tokenOf(getState);
    if (!token) return;
    try {
      const mine = await Services.GetMyImportJobs(token, sessionSince);
      dispatch({ type: IMPORT_JOBS_UPDATED, jobs: mine, notify: true });
    } catch {
      // sin aviso de lo anterior; lo nuevo se sigue igual
    }
    if (hasActiveJobs(getState)) schedule(dispatch, getState);
  },

  /** Sigue un job (iniciado en el asistente, seguido por 409 o activo en la grilla) y arranca la consulta. */
  TrackImportJob:
    (job: ProviderImportJob, notify = true): ImportJobsThunk<void> =>
    (dispatch, getState) => {
      dispatch({ type: IMPORT_JOBS_TRACK, job, notify });
      if (hasActiveJobs(getState)) schedule(dispatch, getState);
    },

  /** Consulta ya (sin esperar la vuelta). */
  PollImportJobs: (): ImportJobsThunk<Promise<void>> => async (dispatch, getState) => {
    await pollOnce(dispatch, getState);
  },

  StartImportJobsPolling: (): ImportJobsThunk<void> => (dispatch, getState) => {
    if (hasActiveJobs(getState)) schedule(dispatch, getState);
  },

  StopImportJobsPolling: (): ImportJobsThunk<void> => (dispatch) => stop(dispatch),

  /** El asistente muestra (o dejó de mostrar) el job. */
  SetImportJobForeground:
    (jobId: string, foreground: boolean): ImportJobsThunk<void> =>
    (dispatch) => {
      dispatch({ type: IMPORT_JOBS_FOREGROUND, jobId, foreground });
    },

  /** El fin del job ya se avisó: no se repite (sessionStorage). */
  AcknowledgeImportJobNotice:
    (jobId: string): ImportJobsThunk<void> =>
    (dispatch, getState) => {
      dispatch({ type: IMPORT_JOBS_NOTICE_DONE, jobId });
      saveNotifiedJobIds(getState().ImportJobs?.notifiedIds ?? [jobId]);
    },

  /** Un job por id (paso 3 del asistente). */
  LoadImportJob:
    (jobId: string): ImportJobsThunk<Promise<ProviderImportJob | undefined>> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetImportJob(token, jobId)),

  /** Historial del proveedor (más nuevos primero). */
  LoadProviderImportJobs:
    (providerId: number, take?: number): ImportJobsThunk<Promise<ProviderImportJob[]>> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetProviderImportJobs(token, providerId, take)),
};

/** Sólo pruebas: corta el timer y olvida el estado del módulo. */
export const resetImportJobsPollingForTests = (): void => {
  pollGeneration++;
  if (pollTimer !== undefined) clearTimeout(pollTimer);
  pollTimer = undefined;
};
